import { useState, useEffect } from 'react';
import {
  fetchMonthlyReportLink,
  DEFAULT_MONTHLY_REPORT_LINK,
} from '../utils/fetchMonthlyReportLink';

/**
 * Custom hook to fetch and manage the Monthly UBT Reports link.
 * Loads the link from MonthlyReport.txt file on component mount.
 * Falls back to a default link if the fetch fails.
 * 
 * @returns {{
 *   link: string,
 *   loading: boolean,
 *   error: Error | null
 * }} An object containing the link, loading state, and any error that occurred.
 */
export function useMonthlyReportLink() {
  const [link, setLink] = useState<string>(DEFAULT_MONTHLY_REPORT_LINK);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    let isMounted = true;

    const loadLink = async () => {
      try {
        setLoading(true);
        const fetchedLink = await fetchMonthlyReportLink();
        
        if (isMounted) {
          setLink(fetchedLink);
          setError(null);
        }
      } catch (err) {
        if (isMounted) {
          setError(err instanceof Error ? err : new Error('Unknown error'));
          // Keep default link on error
          setLink(DEFAULT_MONTHLY_REPORT_LINK);
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    loadLink();

    return () => {
      isMounted = false;
    };
  }, []);

  return { link, loading, error };
}
